import { useTranslation } from "react-i18next";
import InfiniteScroll from "react-infinite-scroll-component";
import NotificationCard from "./NotificationCard";
import IconComponent from "@CubeComponents/icons";
import CheckBoxComponent from "@CubeComponents/Mui_Form/CheckBox";
import { useAllNotification } from "./useAllNotification";

const SCROLL_TARGET_ID = "notifications-scroll-container";

interface Props {
  closeNotificationModal: () => void;
}

function AllNotification({ closeNotificationModal }: Props) {
  const { t } = useTranslation();
  const {
    notifications,
    showEmptyState,
    iconTypeMap,
    hasMore,
    loadMore,
    dismissNotification,
    markGroupRead,
    selectedIds,
    toggleSelect,
    allSelected,
    selectAll,
    clearSelection,
    markSelected,
  } = useAllNotification();

  return (
    <>
      {selectedIds.size > 0 && (
        <div className="flex items-center justify-between px-4 py-2 bg-white">
          <CheckBoxComponent
            label={t("Select All")}
            checked={allSelected}
            indeterminate={selectedIds.size > 0 && !allSelected}
            onChange={(_args, checked) => (checked ? selectAll() : clearSelection())}
          />
          <button
            type="button"
            onClick={markSelected}
            className="flex items-center gap-2 rounded-[0.3125rem] px-3.5 py-2 text-sm font-semibold text-[#192048] cursor-pointer transition hover:bg-[#e8f1fc]"
          >
            {t("Mark all selected as Read")}
          </button>
        </div>
      )}

      <div id={SCROLL_TARGET_ID} className="flex-1 overflow-y-auto overscroll-none px-4 pb-4">
        {showEmptyState ? (
          <div className="flex h-full flex-col items-center justify-center gap-4 p-4">
            <IconComponent name="NoData" width="15rem" height="15rem" className="shrink-0" />
            <p className="text-center text-2xl font-semibold text-[#666a84]">{t("No Notifications")}</p>
          </div>
        ) : (
          <>
            {/* Behind-cap (below the headers): fills the rounded top-corner
                notches of the pinned header with white so the rows' side
                borders don't show through them. Negative margin keeps it out
                of layout flow so it doesn't add to the top gap. */}
            <div className="sticky top-0 z-[5] -mb-7 h-7 bg-white" />
            {/* Front-cap (above the headers): holds the top gap and masks rows
                (and the outgoing header) scrolling behind the pinned header. */}
            <div className="sticky top-0 z-20 h-4 bg-white" />
            <InfiniteScroll
              dataLength={notifications.length}
              next={loadMore}
              hasMore={hasMore}
              style={{ overflow: "visible" }}
              loader={
                <div className="flex justify-center py-3">
                  <IconComponent name="loader-icon" className="animate-spin" />
                </div>
              }
              endMessage={
                notifications.length > 0 ? (
                  <div className="text-center text-xs text-gray-400 py-3">{t("You're all caught up")}</div>
                ) : null
              }
              scrollableTarget={SCROLL_TARGET_ID}
              scrollThreshold="90%"
            >
              <NotificationCard
                notifications={notifications}
                iconTypeMap={iconTypeMap}
                dismissNotification={dismissNotification}
                closeNotificationModal={closeNotificationModal}
                onMarkGroupRead={markGroupRead}
                selectedIds={selectedIds}
                onToggleSelect={toggleSelect}
              />
            </InfiniteScroll>
          </>
        )}
      </div>
    </>
  );
}

export default AllNotification;
